import {
  Button,
  ButtonVariant,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Grid,
  Text,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import React, { useEffect, useState } from 'react'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import { getLanguageLabel } from '../../../utils/HelperFunctions'
import FilePicker from '../../Questionnaire/DocxImport/DocxFileInput/FilePicker'
import useDocxProcessor from '../../Questionnaire/DocxImport/DocxProcessor/DocxProcessor.hooks'
import { MarketSpecificExportError } from '../../Questionnaire/DocxImport/DocxProcessor/DocxProcessor.utils'
import { MarketVisibilityFilter } from '../../Questionnaire/MarketVisibility/marketVisibility'
import { useSaveTranslation } from '../hooks/useSaveTranslation'
import { useSaveTranslationsViaImport } from '../hooks/useSaveTranslationsViaImport'
import { isStoredTranslation } from '../lib/buildTranslationImportEntries'
import {
  dispatchAudienceFieldSave,
  dispatchSurveyFieldSave,
  isPerFieldOnlyKind,
  orderPerFieldSaves
} from '../lib/dispatchFieldSave'
import {
  AudienceTranslatableField,
  CustomAudienceTranslationGroup
} from '../lib/extractAudienceFields'
import {
  TranslatableField,
  extractAllSurveyFields
} from '../lib/extractTranslatableFields'
import { projectDocxToTranslations } from '../lib/projectDocxToTranslations'

interface Props {
  open: boolean
  onClose: () => void
  // The active language on the Translations route. The dropped file's
  // contents are saved as this language's translation — there is no
  // language column in the file itself, so the dialog is per-language.
  targetLanguage: string
  entries: ReadonlyArray<DraftQuestionnaireEntry>
  // Same scope the editor rendered, so the import never writes an option this
  // language's markets don't show.
  isVisible?: MarketVisibilityFilter
  audienceGroups?: ReadonlyArray<CustomAudienceTranslationGroup>
}

interface ImportSummary {
  // Fields written for `targetLanguage`, or already holding the file's text.
  imported: number
  // Live fields the dropped file had no value for. Expected whenever the
  // translator hasn't filled every cell — not surfaced as a problem, just
  // counted so the result text is honest.
  skippedNoValue: number
  // Positions present in the .docx that don't match any live field — the
  // most common cause is the survey having been edited (rename / reorder /
  // delete) between export and import.
  extraKeysInDoc: string[]
  // Per-save errors from the mutation hook. Sequential apply, same shape
  // as the CSV dialog used to surface.
  errors: string[]
}

type Field = TranslatableField | AudienceTranslatableField

const isAudienceField = (f: Field): f is AudienceTranslatableField =>
  f.kind === 'audienceQuestionText' || f.kind === 'audienceOptionValue'

const ImportLanguageDocxDialog: React.FC<Props> = ({
  open,
  onClose,
  targetLanguage,
  entries,
  isVisible,
  audienceGroups = []
}) => {
  const save = useSaveTranslation()
  const { commit } = useSaveTranslationsViaImport()
  const parseDocx = useDocxProcessor({
    includeTranslationOnlyTypes: true,
    preserveEmptyLines: true
  })
  const [busy, setBusy] = useState(false)
  const [summary, setSummary] = useState<ImportSummary | null>(null)
  // Live progress during the persist loop. Mutations run sequentially so
  // researchers get visible feedback (e.g. "3 / 48") rather than a frozen
  // dialog while a big survey writes through.
  const [progress, setProgress] = useState<{
    total: number
    done: number
  } | null>(null)

  // Reset transient state every time the dialog opens so the previous run's
  // summary / progress / file-picker selection never leaks across opens.
  useEffect(() => {
    if (open) {
      setSummary(null)
      setBusy(false)
      setProgress(null)
    }
  }, [open])

  const buildFieldLookup = (): Map<string, Field> => {
    const fieldsByKey = new Map<string, Field>()
    extractAllSurveyFields(entries, isVisible).forEach((field) => {
      fieldsByKey.set(field.key, field)
    })
    audienceGroups.forEach((group) => {
      group.fields.forEach((field) => {
        fieldsByKey.set(field.key, field)
      })
    })
    return fieldsByKey
  }

  // A single field's write. Question/Matrix fields are handled in bulk by the
  // import call in `handleFile`; this covers the fields that still go
  // one-at-a-time (text cards, audience, and the rare import residual).
  const persistField = async (
    field: Field,
    translation: string
  ): Promise<void> => {
    if (isAudienceField(field)) {
      await dispatchAudienceFieldSave(save, field, translation, targetLanguage)
      return
    }
    await dispatchSurveyFieldSave(save, field, translation, targetLanguage)
  }

  const handleFile = async (file: File) => {
    setBusy(true)
    setSummary(null)
    setProgress(null)
    const errors: string[] = []
    let imported = 0
    let skippedNoValue = 0
    const extraKeysInDoc: string[] = []

    try {
      const fieldsByKey = buildFieldLookup()
      const rawEntries = await parseDocx(file)
      const docTexts = projectDocxToTranslations(rawEntries)

      // Split the writes: Question/Matrix fields go through a single
      // importQuestionnaireV2 call; every other entry type + audience (which
      // import can't carry safely — COR-138) stays one-at-a-time. Empty doc
      // text = no value
      // at this position; counted as skipped, no mutation fired. Doc keys with
      // no matching live field are reported as extras.
      const importableJobs: Array<{
        field: TranslatableField
        translation: string
      }> = []
      const perFieldJobs: Array<{ field: Field; translation: string }> = []
      fieldsByKey.forEach((field, key) => {
        const text = docTexts.get(key)
        if (text === undefined || text === '') {
          skippedNoValue += 1
          return
        }
        if (isStoredTranslation(field, targetLanguage, text)) {
          imported += 1
          return
        }
        if (!isAudienceField(field) && !isPerFieldOnlyKind(field.kind)) {
          importableJobs.push({ field, translation: text })
        } else {
          perFieldJobs.push({ field, translation: text })
        }
      })
      docTexts.forEach((_, key) => {
        if (!fieldsByKey.has(key)) extraKeysInDoc.push(key)
      })

      const total = importableJobs.length + perFieldJobs.length
      const errMsg = (e: unknown) =>
        e instanceof Error ? e.message : String(e)
      setProgress({ total, done: 0 })
      let done = 0

      // Question / Matrix — one atomic import call. Anything the import didn't
      // write (residual title-less entries) or a failed call falls back to the
      // per-field save so no translation is silently lost.
      if (importableJobs.length > 0) {
        const overrides = new Map(
          importableJobs.map((job) => [job.field.key, job.translation])
        )
        const persistUnwritten = async (consumed?: Set<string>) => {
          for (const job of importableJobs) {
            if (consumed?.has(job.field.key)) {
              imported += 1
              continue
            }
            try {
              await persistField(job.field, job.translation)
              imported += 1
            } catch (e) {
              errors.push(`${job.field.key}: ${errMsg(e)}`)
            }
          }
        }
        try {
          const result = await commit(entries, targetLanguage, {
            overrides,
            isVisible
          })
          await persistUnwritten(result.consumedKeys)
        } catch (e) {
          errors.push(`Import failed: ${errMsg(e)}`)
          await persistUnwritten()
        }
        done += importableJobs.length
        setProgress({ total, done })
      }

      // Everything import can't carry + audience — per-field. Conjoint
      // attribute names have to be written after their levels; see
      // `orderPerFieldSaves`.
      const orderedPerFieldJobs = orderPerFieldSaves(perFieldJobs)
      for (let i = 0; i < orderedPerFieldJobs.length; i += 1) {
        const job = orderedPerFieldJobs[i]
        try {
          await persistField(job.field, job.translation)
          imported += 1
        } catch (e) {
          errors.push(`${job.field.key}: ${errMsg(e)}`)
        }
        done += 1
        setProgress({ total, done })
      }
    } catch (e) {
      errors.push(
        e instanceof MarketSpecificExportError
          ? e.message
          : `Parse error: ${e instanceof Error ? e.message : String(e)}`
      )
    }

    setSummary({ imported, skippedNoValue, extraKeysInDoc, errors })
    setProgress(null)
    setBusy(false)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Import translation for ${getLanguageLabel(targetLanguage)}`}
      variant={DialogVariant.NoButtons}
      maxWidth={DialogMaxWidth.Md}
    >
      <Grid container direction="column" spacing={2} style={{ padding: 16 }}>
        <Grid item>
          <Text size={TextSize.s}>
            Drop a <strong>.docx</strong> file containing the translated survey.
            Every text it carries is saved as the{' '}
            <strong>{getLanguageLabel(targetLanguage)}</strong> translation —
            switch the active language in the sidebar first if you meant a
            different one.
          </Text>
          <Text size={TextSize.s}>
            The simplest way to produce the file is to export the survey as{' '}
            <em>.docx</em> from the Actions menu, hand it to the translator, and
            have them overwrite text in place. Keep the <code>Q1</code> /{' '}
            <code>A1</code> prefixes and the order of options / rows / columns
            intact — translations are matched position-by-position.
          </Text>
          <Text size={TextSize.s}>
            Question-type labels like <code>Single select</code> and{' '}
            <code>Matrix - single select</code> must stay in English; if a
            translator localises them, that question is silently dropped and
            shows up only as missing in the result.
          </Text>
        </Grid>
        <Grid item>
          <FilePicker
            onFileSelected={(file) => {
              if (!busy) handleFile(file)
            }}
          />
        </Grid>
        {busy && (
          <Grid item>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              {progress
                ? `Importing… ${progress.done} / ${progress.total}`
                : 'Parsing file…'}
            </Text>
            {progress && progress.total > 0 && (
              <div
                style={{
                  marginTop: 4,
                  height: 6,
                  background: '#e0e6e9',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${Math.round(
                      (progress.done / progress.total) * 100
                    )}%`,
                    height: '100%',
                    background: '#2f6fed',
                    transition: 'width 120ms linear'
                  }}
                />
              </div>
            )}
          </Grid>
        )}
        {summary && (
          <Grid item>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              Result
            </Text>
            <Text size={TextSize.s}>
              Imported: {summary.imported} field
              {summary.imported === 1 ? '' : 's'} for{' '}
              {getLanguageLabel(targetLanguage)}
            </Text>
            {summary.skippedNoValue > 0 && (
              <Text size={TextSize.s}>
                {summary.skippedNoValue} field
                {summary.skippedNoValue === 1 ? '' : 's'} skipped (no
                translation in the file)
              </Text>
            )}
            {summary.imported === 0 &&
              summary.skippedNoValue === 0 &&
              summary.errors.length === 0 && (
                <Text size={TextSize.s}>
                  Nothing to import — the file had no recognisable Q/A entries.
                  Check the question-type labels.
                </Text>
              )}
            {summary.extraKeysInDoc.length > 0 && (
              <>
                <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                  Extra positions in file (not in survey, skipped)
                </Text>
                {summary.extraKeysInDoc.map((key) => (
                  <Text key={key} size={TextSize.s}>
                    {key}
                  </Text>
                ))}
              </>
            )}
            {summary.errors.length > 0 && (
              <>
                <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                  Errors
                </Text>
                {summary.errors.map((err, idx) => (
                  <Text key={idx} size={TextSize.s}>
                    {err}
                  </Text>
                ))}
              </>
            )}
          </Grid>
        )}
        <Grid item>
          <Button onClick={onClose} variant={ButtonVariant.SecondaryOutlined}>
            Close
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default ImportLanguageDocxDialog
